/**
 * Google Analytics Event Tracking
 * Utility functions for tracking user interactions and events
 */

// Firebase lazy-loaded in logEventToFirestore to keep it out of the critical
// chunk (analytics.ts is eager-loaded for trackEvent but firebase is only
// needed once analytics consent has been granted, usually well after first paint).

/**
 * Detect automated/test traffic that should not be tracked.
 * Checks for Playwright, Puppeteer, Cypress, headless browsers,
 * and localhost/preview environments.
 */
const isTestTraffic = (): boolean => {
  if (typeof window === 'undefined') return true;
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('headless') || ua.includes('playwright') || ua.includes('puppeteer') || ua.includes('cypress')) return true;
  if ((window as any).__playwright || (window as any).__pw_manual) return true;
  if (navigator.webdriver) return true;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.includes('--')) return true;
  return false;
};

const SESSION_ID_KEY = 'fitd-analytics-session-id';
const CONSENT_STORAGE_KEY = 'fitd-cookie-consent';
const RATE_LIMIT_KEY = 'fitd-analytics-event-count';
const ATTRIBUTION_KEY = 'fitd-analytics-attribution';
const USER_ROLE_KEY = 'fitd-analytics-user-role';
const USER_EMAIL_KEY = 'fitd-analytics-user-email';
const RATE_LIMIT_MAX_PER_SESSION = 50;
const FIRESTORE_TTL_DAYS = 90;

const INTERNAL_ROLES = new Set(['admin', 'support', 'finance', 'tech']);
const INTERNAL_EMAIL_SUFFIX = '@test.footinthedoor.ch';

/**
 * Internal/admin traffic exclusion. Role + email are cached in sessionStorage
 * by AuthContext so synchronous trackers can read them without an async
 * Firestore round-trip. Excluding internal team + E2E test users keeps GA4
 * and the activation funnel free of noise (3rd consecutive daily-brief signal,
 * 2026-05-25).
 *
 * Roles aligned with Dashboard.tsx:215.
 * Email suffix aligned with tests/teardown-test-user.ts wildcard.
 */
const isAdminTraffic = (): boolean => {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const role = sessionStorage.getItem(USER_ROLE_KEY);
    if (role && INTERNAL_ROLES.has(role)) return true;
    const email = sessionStorage.getItem(USER_EMAIL_KEY);
    if (email && email.endsWith(INTERNAL_EMAIL_SUFFIX)) return true;
    return false;
  } catch {
    return false;
  }
};

/** Traffic that must never reach GA4 or Firestore analytics. */
const isExcludedTraffic = (): boolean => isTestTraffic() || isAdminTraffic();

/**
 * Persist the current user's role for {@link isAdminTraffic}. Called from
 * AuthContext on every auth state change; pass null on logout to clear.
 */
export const cacheAnalyticsUserRole = (role: string | null): void => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (role) sessionStorage.setItem(USER_ROLE_KEY, role);
    else sessionStorage.removeItem(USER_ROLE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Persist the current user's email for {@link isAdminTraffic}. Set
 * synchronously on auth state change (no Firestore round-trip needed) so
 * @test.footinthedoor.ch traffic is excluded from the very first post-login
 * event. Pass null on logout to clear.
 */
export const cacheAnalyticsUserEmail = (email: string | null): void => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (email) sessionStorage.setItem(USER_EMAIL_KEY, email);
    else sessionStorage.removeItem(USER_EMAIL_KEY);
  } catch {
    /* ignore */
  }
};

export type TrafficSource = 'googleads' | 'organic_google' | 'organic_other' | 'direct' | 'social' | 'referral' | 'email' | 'unknown';

export interface AttributionData {
  gclid?: string;
  gbraid?: string; // Google Ads iOS Enhanced Conversions (app)
  wbraid?: string; // Google Ads iOS Enhanced Conversions (web-to-app)
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  trafficSource: TrafficSource;
  landingPath: string;
  capturedAt: number;
}

/**
 * Extract a Google Ads click identifier (gclid/gbraid/wbraid) from a URL string.
 * Returns the value if any of the three params is present, else undefined.
 * Used to fall back on document.referrer when the user landed on `/` with the
 * param then navigated client-side (Apple ATT iOS flow drops gbraid in the URL
 * which gets lost on internal nav unless captured at first page).
 */
function extractGoogleAdsIdsFromUrl(url: string): { gclid?: string; gbraid?: string; wbraid?: string } {
  try {
    const u = new URL(url);
    return {
      gclid: u.searchParams.get('gclid') || undefined,
      gbraid: u.searchParams.get('gbraid') || undefined,
      wbraid: u.searchParams.get('wbraid') || undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Capture and persist attribution data for the session.
 *
 * Reads gclid + utm_* from URL on landing, falls back to referrer-based
 * detection when no params are present. Persists to sessionStorage so all
 * events in the session inherit the same attribution — meaning a user who
 * arrives via ?gclid=XYZ on /score and then navigates to /pricing still
 * has every event attributed to Google Ads.
 */
function getAttribution(): AttributionData {
  if (typeof window === 'undefined') {
    return { trafficSource: 'unknown', landingPath: '', capturedAt: Date.now() };
  }
  // First, check if URL has fresh attribution params (means new landing)
  const params = new URLSearchParams(window.location.search);
  let gclid = params.get('gclid') || undefined;
  let gbraid = params.get('gbraid') || undefined;
  let wbraid = params.get('wbraid') || undefined;
  const utm_source = params.get('utm_source') || undefined;
  const utm_medium = params.get('utm_medium') || undefined;
  const utm_campaign = params.get('utm_campaign') || undefined;
  const utm_term = params.get('utm_term') || undefined;
  const utm_content = params.get('utm_content') || undefined;

  // Fallback: pull Google Ads click ids from referrer when current URL has none.
  // Covers the iOS case where the user lands on `/` with ?gbraid=... then the
  // SPA navigates to /onboarding — the param is no longer in the URL but is
  // still in document.referrer for subsequent events in the same session.
  const referrer = document.referrer;
  if (!gclid && !gbraid && !wbraid && referrer) {
    const fromRef = extractGoogleAdsIdsFromUrl(referrer);
    gclid = fromRef.gclid;
    gbraid = fromRef.gbraid;
    wbraid = fromRef.wbraid;
  }

  // Treat any UTM/click-id presence as a "fresh landing" — covers Google Ads
  // campaigns where ValueTrack adds `utm_campaign` (or `utm_content`/`utm_term`)
  // without an explicit `utm_source`. Without this, a user who lands on
  // `/?utm_campaign=test_zones_frontalieres_74_01` would never persist the
  // campaign name, breaking attribution for the geo-test.
  const hasFreshParams = !!(
    gclid || gbraid || wbraid ||
    utm_source || utm_medium || utm_campaign || utm_term || utm_content
  );

  if (!hasFreshParams) {
    // Reuse stored attribution if any
    try {
      const stored = sessionStorage.getItem(ATTRIBUTION_KEY);
      if (stored) return JSON.parse(stored) as AttributionData;
    } catch { /* ignore */ }
  }

  // Compute trafficSource
  let trafficSource: TrafficSource = 'unknown';
  if (gclid || gbraid || wbraid || (utm_source === 'google' && utm_medium === 'cpc')) {
    trafficSource = 'googleads';
  } else if (utm_source) {
    trafficSource = utm_source.toLowerCase().includes('linkedin') ? 'social' : 'referral';
  } else if (!referrer) {
    trafficSource = 'direct';
  } else {
    try {
      const refHost = new URL(referrer).hostname;
      if (refHost.includes('google.')) trafficSource = 'organic_google';
      else if (refHost.includes('bing.') || refHost.includes('duckduckgo.') || refHost.includes('yahoo.')) trafficSource = 'organic_other';
      else if (refHost.includes('linkedin.') || refHost.includes('facebook.') || refHost.includes('instagram.') || refHost.includes('twitter.') || refHost.includes('x.com') || refHost.includes('tiktok.')) trafficSource = 'social';
      else if (refHost.includes(window.location.hostname)) trafficSource = 'direct'; // internal nav
      else trafficSource = 'referral';
    } catch { /* malformed referrer */ }
  }

  const data: AttributionData = {
    gclid, gbraid, wbraid, utm_source, utm_medium, utm_campaign, utm_term, utm_content,
    trafficSource,
    landingPath: window.location.pathname,
    capturedAt: Date.now(),
  };

  try { sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data)); } catch { /* ignore */ }
  return data;
}

/**
 * Public accessor for the attribution captured at session start.
 *
 * Returns the full AttributionData object (UTM + click ids + trafficSource +
 * landingPath + capturedAt). Use this from the signup flow to persist
 * `firstTouch` on `users/{uid}` (see `persistFirstTouch`).
 *
 * Safe to call from any client component — falls back to a synthetic `unknown`
 * payload when called server-side or before any landing was captured.
 */
export const getStoredAttribution = (): AttributionData => getAttribution();

/**
 * High-frequency behavioural events that stay on gtag only.
 * They don't participate in the activation funnel and would
 * flood Firestore without actionable value.
 */
const FIRESTORE_DUAL_LOG_BLACKLIST = new Set([
  'scroll_depth',
  'page_time',
  'external_link_click',
  'view_feature',
  'view_item',
  'social_proof_view',
  // `checkout_started` est désormais loggé côté serveur dans la Cloud Function
  // `createCheckoutSession` (source de vérité, immune navigation/adblock). Le
  // dual-log client est tiré juste avant `window.location.href = stripeUrl` →
  // son `addDoc` fire-and-forget est avorté par l'unload (4 sessions Stripe →
  // 1 event en prod). On coupe le write Firestore client pour éviter le double
  // comptage ; gtag/GA4 continue de recevoir l'event (cf. trackEvent).
  'checkout_started',
]);

/**
 * Critical funnel events that bypass the session rate-limit. These are
 * irreplaceable conversion checkpoints — losing any of them breaks
 * attribution and the activation funnel. A user who browses the landing
 * extensively before signup can saturate the 50-event session budget,
 * after which every subsequent event drops silently — including sign_up
 * and purchase. The allowlist guarantees these always make it through.
 */
const CRITICAL_FUNNEL_EVENTS = new Set([
  'sign_up',
  'checkout_started',
  'trial_started',
  'subscription_created',
  'purchase',
  'cv_created',
  'onboarding_completion_pro_clicked',
  'onboarding_completion_free_clicked',
  // Diagnostic events for drop-off analysis — must always be dual-logged
  // so we can reconstruct where users abandon, even on long sessions that
  // would otherwise hit the 50-event rate limit.
  'onboarding_step_abandoned',
  'cv_upload_started',
  'cv_upload_failed',
  'cv_upload_invalid',
  // Signup→checkout funnel diagnostic events (added 2026-05-19) — these
  // reconstruct the 64% drop between signup_completed and checkout_started.
  // Must always pass the rate limit so the funnel stays observable.
  'signup_completed',
  'signup_post_redirect_view',
  'checkout_intent_view',
  'checkout_session_create_attempted',
  'checkout_session_create_succeeded',
  'checkout_session_create_failed',
  'signup_to_checkout_abandoned',
  'score_resume_opened',
  'score_resume_reveal_shown',
  'score_resume_signup_click',
]);

/**
 * Stable per-session UUID for activation funnel reconstruction even
 * when `userId` is null (pre-signup visits).
 */
export function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const c = (typeof crypto !== 'undefined' ? crypto : null) as any;
    const fresh: string = c?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    return 'unknown';
  }
}

/**
 * Per-session write budget. Prevents bots that slip past `isTestTraffic()`
 * from saturating the collection, and caps runaway tracking if a new
 * event gets wired into a tight loop by mistake.
 */
function canDualLogInThisSession(): boolean {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    const current = parseInt(sessionStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
    if (current >= RATE_LIMIT_MAX_PER_SESSION) return false;
    sessionStorage.setItem(RATE_LIMIT_KEY, String(current + 1));
    return true;
  } catch {
    return true;
  }
}

/**
 * Mirrors CookieConsentContext default: no explicit choice = opt-in (niveau
 * « essentiels »). Le dual-log Firestore est de la mesure d'audience first-party
 * anonymisée — même catégorie que Clarity — donc gaté sur `consent.clarity`,
 * PAS sur le marketing Google. Un consentement d'ancienne version (schéma v1
 * sans `clarity`) est traité comme « pas de choix » : le bandeau v2 sera de
 * toute façon re-affiché, on garde l'opt-in essentiels en attendant.
 * Re-reading the localStorage key keeps this pure-ish (no context dep).
 */
function hasAnalyticsConsent(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return true;
    const stored = JSON.parse(raw);
    if (stored?.version !== 2) return true;
    return stored?.consent?.clarity === true;
  } catch {
    return false;
  }
}

/**
 * Dual-log every frontend event to Firestore `analyticsEvents`.
 * Bypasses adblockers that neutralize gtag, so the activation funnel
 * can be reconstructed from Firestore alone.
 * Fire-and-forget: analytics MUST NOT block UI on failure.
 */
function logEventToFirestore(eventName: string, eventParams?: Record<string, any>): void {
  if (isExcludedTraffic() || !hasAnalyticsConsent()) return;
  if (FIRESTORE_DUAL_LOG_BLACKLIST.has(eventName)) return;
  if (!CRITICAL_FUNNEL_EVENTS.has(eventName) && !canDualLogInThisSession()) return;
  const expiresAt = new Date(Date.now() + FIRESTORE_TTL_DAYS * 24 * 60 * 60 * 1000);
  const path = window.location.pathname;
  const referrer = document.referrer || null;
  const userAgent = navigator.userAgent.slice(0, 200);
  const sessionId = getSessionId();
  const attribution = getAttribution();
  void (async () => {
    try {
      const [{ collection, addDoc, serverTimestamp }, { db, auth }] = await Promise.all([
        import('firebase/firestore'),
        import('../firebase'),
      ]);
      await addDoc(collection(db, 'analyticsEvents'), {
        type: eventName,
        userId: auth.currentUser?.uid ?? null,
        sessionId,
        payload: eventParams ?? {},
        path,
        referrer,
        source: 'frontend',
        userAgent,
        attribution: {
          trafficSource: attribution.trafficSource,
          ...(attribution.gclid && { gclid: attribution.gclid }),
          ...(attribution.gbraid && { gbraid: attribution.gbraid }),
          ...(attribution.wbraid && { wbraid: attribution.wbraid }),
          ...(attribution.utm_source && { utm_source: attribution.utm_source }),
          ...(attribution.utm_medium && { utm_medium: attribution.utm_medium }),
          ...(attribution.utm_campaign && { utm_campaign: attribution.utm_campaign }),
          ...(attribution.utm_term && { utm_term: attribution.utm_term }),
          ...(attribution.utm_content && { utm_content: attribution.utm_content }),
          landingPath: attribution.landingPath,
        },
        createdAt: serverTimestamp(),
        expiresAt,
      });
    } catch {
      /* silent fail */
    }
  })();
}

/**
 * Initialize Google Analytics (called after gtag is loaded)
 */
export const initializeAnalytics = () => {
  if (typeof window !== 'undefined' && (window as any).gtag && !isExcludedTraffic()) {
    const gtag = (window as any).gtag;

    // Log a page view
    gtag('config', 'G-YEL11ZFMBW', {
      page_path: window.location.pathname,
      page_title: document.title,
      anonymize_ip: true,
    });
  }
};

/**
 * Track a custom event.
 * Dispatches to gtag (when available) AND mirrors to Firestore
 * (`analyticsEvents`) so the funnel stays observable even when
 * adblockers or GA4 misconfig drop the gtag path.
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window === 'undefined' || isExcludedTraffic()) return;

  if ((window as any).gtag) {
    const gtag = (window as any).gtag;
    gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
    });
  }

  logEventToFirestore(eventName, eventParams);
};

/**
 * Track when user signs up.
 *
 * @param method - 'google' | 'email' | other provider key
 * @param source - Optional provenance tag (e.g. 'score_page', 'onboarding',
 *   'direct'). Lets us segment the `score_complete → sign_up` funnel by
 *   landing surface without joining naïvement les events au timestamp.
 *   When undefined, the `source` key is omitted from the payload (so old
 *   dashboards/queries that don't expect it stay stable).
 */
export const trackSignUp = (method: string = 'google', source?: string) => {
  const payload: Record<string, any> = { method };
  if (source) payload.source = source;
  trackEvent('sign_up', payload);
  // Google Ads conversion (primary, drives bidding optimization).
  trackGoogleAdsConversion('signup');
};

/**
 * Track when user signs in
 */
export const trackSignIn = (method: string = 'google') => {
  trackEvent('login', {
    method,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName: string) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
  });
};

/**
 * Track feature views
 */
export const trackFeatureView = (featureName: string) => {
  trackEvent('view_feature', {
    feature_name: featureName,
  });
};

/**
 * Track pricing plan view
 */
export const trackPricingView = (planName?: string) => {
  trackEvent('view_item', {
    content_type: 'pricing_plan',
    item_id: planName || 'pricing_section',
  });
};

/**
 * Track trial signup
 */
export const trackTrialSignup = () => {
  trackEvent('trial_signup', {
    trial_duration: '7 days',
  });
};

/**
 * Track page scroll depth (for engagement tracking)
 */
export const trackScrollDepth = (scrollPercentage: number) => {
  trackEvent('scroll_depth', {
    scroll_percentage: scrollPercentage,
  });
};

/**
 * Track time on page
 */
export const trackPageTime = (pageUrl: string, timeInSeconds: number) => {
  trackEvent('page_time', {
    page_url: pageUrl,
    time_seconds: timeInSeconds,
  });
};

/**
 * Track file downloads
 */
export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName: string, status: 'success' | 'error') => {
  trackEvent('form_submit', {
    form_name: formName,
    status,
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLinkClick = (url: string, text: string) => {
  trackEvent('external_link_click', {
    link_url: url,
    link_text: text,
  });
};

/**
 * Track exception/errors
 */
export const trackException = (description: string, fatal: boolean = false) => {
  trackEvent('exception', {
    description,
    fatal,
  });
};

/**
 * Setup automatic scroll depth tracking
 */
export const setupScrollDepthTracking = () => {
  const scrollThresholds = [25, 50, 75, 100];
  let trackedThresholds: number[] = [];

  const handleScroll = () => {
    const scrollPercentage =
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

    scrollThresholds.forEach((threshold) => {
      if (
        scrollPercentage >= threshold &&
        !trackedThresholds.includes(threshold)
      ) {
        trackedThresholds.push(threshold);
        trackScrollDepth(threshold);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

/**
 * Setup automatic page time tracking
 */
export const setupPageTimeTracking = () => {
  const startTime = Date.now();
  const pageUrl = window.location.pathname;

  const handleBeforeUnload = () => {
    const timeInSeconds = Math.floor((Date.now() - startTime) / 1000);
    if (timeInSeconds > 10) {
      // Only track if user spent more than 10 seconds
      trackPageTime(pageUrl, timeInSeconds);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};

// ============================================
// FREEMIUM & PAYWALL EVENTS (New)
// ============================================

/**
 * Track when user creates a CV.
 * cvId may be null when the creation happens via a backend webhook (e.g. PDF
 * re-upload) and the draft id isn't yet known client-side.
 */
export const trackCVCreated = (cvId: string | null, template?: string) => {
  trackEvent('cv_created', {
    cv_id: cvId ?? null,
    template: template || 'default',
  });
};

/**
 * Track when user attempts to download CV (may trigger paywall)
 */
export const trackCVDownloadAttempted = (cvId: string, hasPremium: boolean) => {
  trackEvent('cv_download_attempted', {
    cv_id: cvId,
    has_premium: hasPremium,
  });
};

/**
 * Track when CV Export Paywall is shown
 * CRITICAL: This is a conversion point
 */
export const trackCVExportPaywallShown = (cvId: string) => {
  trackEvent('cv_export_paywall_shown', {
    cv_id: cvId,
    source: 'cv_export',
  });
};

/**
 * Track when user converts from CV Export Paywall
 * CRITICAL: This is a conversion event
 */
export const trackCVExportPaywallConverted = (cvId: string) => {
  trackEvent('cv_export_paywall_converted', {
    cv_id: cvId,
    source: 'cv_export',
    value: 0,
    currency: 'CHF',
  });
};

/**
 * Track when user starts a job analysis
 */
export const trackAnalysisStarted = (analysisId: string, hasPremium: boolean) => {
  trackEvent('analysis_started', {
    analysis_id: analysisId,
    has_premium: hasPremium,
  });
};

/**
 * Track when analysis score is shown to user
 */
export const trackAnalysisScoreShown = (
  analysisId: string,
  score: number,
  hasPremium: boolean
) => {
  const scoreRange = score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low';

  trackEvent('analysis_score_shown', {
    analysis_id: analysisId,
    score,
    score_range: scoreRange,
    has_premium: hasPremium,
  });
};

/**
 * Track when user clicks on a locked (blurred) AI suggestion
 */
export const trackAISuggestionLockedClicked = (
  analysisId: string,
  suggestionsCount: number
) => {
  trackEvent('ai_suggestion_locked_clicked', {
    analysis_id: analysisId,
    suggestions_count: suggestionsCount,
  });
};

/**
 * Track when AI Suggestions Paywall is shown
 * CRITICAL: This is a conversion point
 */
export const trackAISuggestionsPaywallShown = (
  suggestionsCount: number,
  currentScore?: number
) => {
  trackEvent('ai_suggestions_paywall_shown', {
    suggestions_count: suggestionsCount,
    current_score: currentScore,
  });
};

/**
 * Track when user converts from AI Suggestions Paywall
 * CRITICAL: This is a conversion event
 */
export const trackAISuggestionsPaywallConverted = (suggestionsCount: number) => {
  trackEvent('ai_suggestions_paywall_converted', {
    source: 'ai_suggestions',
    suggestions_count: suggestionsCount,
    value: 0,
    currency: 'CHF',
  });
};

/**
 * Track when Job Analysis Paywall is shown
 * CRITICAL: This is a conversion point
 */
export const trackJobAnalysisPaywallShown = (trigger: 'modal_open' | 'launch_click' | 'backend_error' = 'modal_open') => {
  trackEvent('job_analysis_paywall_shown', {
    source: 'job_analysis',
    trigger,
  });
};

/**
 * Track when user converts from Job Analysis Paywall
 * CRITICAL: This is a conversion event
 */
export const trackJobAnalysisPaywallConverted = (trigger: 'modal_open' | 'launch_click' | 'backend_error' = 'modal_open') => {
  trackEvent('job_analysis_paywall_converted', {
    source: 'job_analysis',
    trigger,
    value: 0,
    currency: 'CHF',
  });
};

/**
 * Track when user starts the Stripe checkout flow (createCheckoutSession call).
 * CRITICAL: This is the funnel step between `paywall_view` and
 * `trial_started`/`subscription_created`. If this is missing, we cannot
 * distinguish "user never clicked the paywall CTA" from "createCheckoutSession
 * failed/bounced". Fire from EVERY entry point that calls
 * `createCheckoutSession` (onboarding Step7, /pricing SubscriptionBuyButton,
 * any other paywall surface).
 *
 * @param source - Surface that initiated the checkout. Used to attribute drop-off
 *                 by funnel position. Allowed values:
 *                 - `onboarding_completion` (Step7 of onboarding flow)
 *                 - `pricing_page` (SubscriptionBuyButton on /pricing)
 *                 - `paywall_modal` (in-app paywall modal)
 *                 - `feature_gate` (FeatureGate component)
 *                 - `trial_expiring_banner` (TrialExpiringBanner)
 *                 - `other` (fallback)
 * @param tier - Tier the user is checking out for (`trial_7d`, `pro_monthly`,
 *               `pro_yearly`, etc.)
 */
export const trackCheckoutStarted = (
  source:
    | 'onboarding_completion'
    | 'pricing_page'
    | 'paywall_modal'
    | 'feature_gate'
    | 'trial_expiring_banner'
    | 'other',
  tier: string,
  extra?: Record<string, unknown>
) => {
  trackEvent('checkout_started', {
    source,
    tier,
    plan: tier, // back-compat with the legacy StripeCheckoutButton payload
    ...(extra || {}),
  });
};

/**
 * Track when user starts trial
 * CRITICAL: This is a conversion event
 */
export const trackTrialStarted = (plan: string = 'trial_14d', price: number = 0) => {
  trackEvent('trial_started', {
    plan,
    price,
    currency: 'CHF',
  });

  // Google Ads conversion — trial start is a paid-intent signal (Stripe pre-auth
  // already requires CB at signup). Sending the expected post-trial subscription
  // value (29.90 CHF for the default trial_7d → pro_monthly path) densifies the
  // smart bidding signal between checkout_started and subscription_created.
  trackGoogleAdsConversion('trial_started', {
    value: 29.90,
    currency: 'CHF',
  });
};

/**
 * Track when subscription is created (after payment)
 * CRITICAL: This is a conversion event
 */
export const trackSubscriptionCreated = (
  subscriptionId: string,
  tier: string,
  price: number
) => {
  trackEvent('subscription_created', {
    subscription_id: subscriptionId,
    tier,
    price,
    currency: 'CHF',
  });

  // Also track as purchase for ecommerce
  trackEvent('purchase', {
    transaction_id: subscriptionId,
    value: price,
    currency: 'CHF',
    items: [
      {
        item_name: tier,
        price: price,
      },
    ],
  });

  // Google Ads conversion — l'ID de la conversion sera fourni par
  // Google Ads après création des actions de conversion. Tant qu'il
  // est null le call est no-op pour ne pas bloquer le funnel.
  trackGoogleAdsConversion('subscription_created', {
    transaction_id: subscriptionId,
    value: price,
    currency: 'CHF',
  });
};

/**
 * Google Ads conversion sender.
 *
 * Conversion IDs are configured in Google Ads (Tools → Conversions).
 * Until populated below, the call is a no-op so we ship the wiring
 * without breaking anything.
 */
const GOOGLE_ADS_CONVERSION_LABELS: Record<string, string> = {
  // Map: conversion_name → 'AW-18122856073/<conversion_label>'
  // Labels are issued by Google Ads when creating a Conversion Action
  // (Tools → Measurement → Conversions → New conversion action).
  signup: 'AW-18122856073/weFLCNi2lqQcEImt08FD', // primary, drives bidding optimization
  trial_started: 'AW-18122856073/WhfaCIvTla0cEImt08FD', // mid-funnel — label re-copied from Google Ads API 2026-05-18; the previous value `WhtaCIyT1a0cEImt08FD` was a typo (3 chars off) and recorded 0 conversions since 2026-05-14.
  subscription_created: 'AW-18122856073/GwaVCOXy4qMcEImt08FD', // secondary, ROAS measurement
};

export const trackGoogleAdsConversion = (
  conversionName: string,
  params?: { transaction_id?: string; value?: number; currency?: string; gclid?: string; gbraid?: string; wbraid?: string }
): void => {
  if (typeof window === 'undefined') return;
  const sendTo = GOOGLE_ADS_CONVERSION_LABELS[conversionName];
  if (!sendTo) return; // Not configured yet — no-op
  const gtag = (window as any).gtag;
  if (!gtag) return;
  // Re-inject the click id captured at landing (kept in sessionStorage via
  // getAttribution) so Google Ads can match the conversion to the ad click
  // even when Consent Mode v2 default-denies `ad_storage` and the `_gcl_aw`
  // cookie is therefore never written. Caller-provided ids take precedence
  // for server-side reconciliation use cases.
  const stored = getAttribution();
  const gclid = params?.gclid ?? stored.gclid;
  const gbraid = params?.gbraid ?? stored.gbraid;
  const wbraid = params?.wbraid ?? stored.wbraid;
  gtag('event', 'conversion', {
    send_to: sendTo,
    ...(params?.value !== undefined && { value: params.value }),
    ...(params?.currency && { currency: params.currency }),
    ...(params?.transaction_id && { transaction_id: params.transaction_id }),
    ...(gclid && { gclid }),
    ...(gbraid && { gbraid }),
    ...(wbraid && { wbraid }),
  });
};

/**
 * Track when user views pricing page
 */
export const trackPricingPageVisit = (source?: string) => {
  trackEvent('page_view', {
    page_name: 'pricing',
    page_path: '/pricing',
    source: source || 'direct',
  });
};

/**
 * Identify user (set user ID for tracking)
 */
export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    const gtag = (window as any).gtag;
    gtag('config', 'G-YEL11ZFMBW', {
      user_id: userId,
    });

    if (traits) {
      gtag('set', 'user_properties', traits);
    }
  }
};

// ============================================
// CONVERSION FUNNEL EVENTS (Epic #458)
// ============================================

import type { ConversionFunnelStage, ChurnPoint, LeadSource } from '../types/conversionFunnel.types';

/**
 * Track progression through the conversion funnel
 */
export const trackFunnelStep = (
  stage: ConversionFunnelStage,
  metadata?: Record<string, any>
) => {
  trackEvent('funnel_step', {
    funnel_stage: stage,
    ...metadata,
  });
};

/**
 * Track lead capture (email captured before signup)
 */
export const trackLeadCaptured = (source: LeadSource | 'popup' | 'inline', email?: string) => {
  trackEvent('lead_captured', {
    lead_source: source,
    has_email: !!email,
  });
};

/**
 * Track churn point (where the user dropped off)
 */
export const trackChurnPoint = (
  churnPoint: ChurnPoint,
  metadata?: Record<string, any>
) => {
  trackEvent('funnel_churn', {
    churn_point: churnPoint,
    ...metadata,
  });
};

/**
 * Track exit intent popup interaction
 */
export const trackExitIntentPopup = (
  action: 'shown' | 'dismissed' | 'converted',
  leadMagnetId?: string
) => {
  trackEvent('exit_intent_popup', {
    action,
    lead_magnet_id: leadMagnetId,
  });
};

/**
 * Track lead magnet download
 */
export const trackLeadMagnetDownload = (magnetId: string, source: LeadSource) => {
  trackEvent('lead_magnet_download', {
    magnet_id: magnetId,
    source,
  });
};

/**
 * Track demo video interaction
 */
export const trackDemoVideo = (
  action: 'play' | 'pause' | 'complete' | 'cta_click',
  percentWatched?: number
) => {
  trackEvent('demo_video', {
    action,
    percent_watched: percentWatched,
  });
};

/**
 * Track referral system v2 events
 */
export const trackReferralEvent = (
  action: 'invite_sent' | 'invite_accepted' | 'reward_earned',
  referralCode?: string
) => {
  trackEvent('referral_v2', {
    action,
    referral_code: referralCode,
  });
};

/**
 * Track blog CTA click
 */
export const trackBlogCTAClick = (blogSlug: string, ctaType: string) => {
  trackEvent('blog_cta_click', {
    blog_slug: blogSlug,
    cta_type: ctaType,
  });
};

/**
 * Track onboarding express progress (3-step onboarding)
 */
export const trackOnboardingExpress = (
  step: number,
  totalSteps: number,
  action: 'started' | 'completed' | 'skipped'
) => {
  trackEvent('onboarding_express', {
    step,
    total_steps: totalSteps,
    action,
  });
};

/**
 * Track social proof interaction
 */
export const trackSocialProofView = (
  proofType: 'testimonial' | 'cv_counter' | 'rating',
  metadata?: Record<string, any>
) => {
  trackEvent('social_proof_view', {
    proof_type: proofType,
    ...metadata,
  });
};

// ============================================
// ONBOARDING & NUDGE EVENTS (Issue #598)
// ============================================

/**
 * Track when user is redirected to CV after onboarding
 */
export const trackOnboardingRedirectToCV = (cvId?: string) => {
  trackEvent('onboarding_redirect_to_cv', {
    cv_id: cvId || 'unknown',
  });
};

/**
 * Track when nudge analysis banner is clicked
 */
export const trackNudgeAnalysisClicked = (score?: number) => {
  trackEvent('nudge_analysis_clicked', {
    score,
  });
};

/**
 * Track when nudge adapt CV banner is clicked
 */
export const trackNudgeAdaptCVClicked = () => {
  trackEvent('nudge_adapt_cv_clicked');
};

/**
 * Track when a nudge is dismissed manually
 */
export const trackNudgeDismissed = (nudgeId: string) => {
  trackEvent('nudge_dismissed', {
    nudge_id: nudgeId,
  });
};

// ============================================
// CV QUOTA & FREE TIER EVENTS
// ============================================

/**
 * Track when CV quota paywall is shown (user hit 3 CV limit)
 * CRITICAL: This is a conversion point
 */
export const trackCVQuotaPaywallShown = (cvCount: number) => {
  trackEvent('cv_quota_paywall_shown', {
    cv_count: cvCount,
    source: 'cv_creation',
  });
};

/**
 * Track when user clicks upgrade from CV quota paywall
 * CRITICAL: This is a conversion event
 */
export const trackCVQuotaPaywallConverted = (cvCount: number) => {
  trackEvent('cv_quota_paywall_converted', {
    cv_count: cvCount,
    source: 'cv_creation',
    value: 0,
    currency: 'CHF',
  });
};

/**
 * Track when user dismisses CV quota paywall ("Plus tard")
 */
export const trackCVQuotaPaywallDismissed = (cvCount: number) => {
  trackEvent('cv_quota_paywall_dismissed', {
    cv_count: cvCount,
  });
};

/**
 * Track free plan pricing CTA click
 */
export const trackFreePlanCTAClick = (source: 'landing' | 'pricing') => {
  trackEvent('free_plan_cta_click', {
    source,
  });
};

// ============================================
// LEAD MAGNET EVENTS
// ============================================

/**
 * Track when the lead capture popup is shown
 */
export const trackLeadPopupShown = (trigger: string = 'auto') => {
  trackEvent('lead_popup_shown', { trigger });
};

/**
 * Track when the lead capture popup is closed without subscribing
 */
export const trackLeadPopupClosed = () => {
  trackEvent('lead_popup_closed');
};

// ============================================
// SIGNUP→CHECKOUT FUNNEL DIAGNOSTIC EVENTS (added 2026-05-19)
// ============================================

export const trackSignupCompleted = (
  method: 'email' | 'google',
  landingPath: string,
  firstTouchSource: string,
  experimentVariants: Record<string, string>
) => {
  trackEvent('signup_completed', { method, landingPath, firstTouchSource, experimentVariants });
};

export const trackSignupPostRedirectView = (
  pageName: string,
  secondsFromSignup: number,
  userId: string
) => {
  trackEvent('signup_post_redirect_view', { pageName, secondsFromSignup, userId });
};

export type CheckoutCtaLocation = 'paywall' | 'onboarding' | 'pricing' | 'header';

export const trackCheckoutIntentView = (ctaLocation: CheckoutCtaLocation, userId: string) => {
  trackEvent('checkout_intent_view', { ctaLocation, userId });
};

export const trackCheckoutSessionCreateAttempted = (
  priceId: string,
  ctaLocation: CheckoutCtaLocation,
  userId: string
) => {
  trackEvent('checkout_session_create_attempted', { priceId, ctaLocation, userId });
};

export const trackCheckoutSessionCreateSucceeded = (
  sessionId: string,
  priceId: string,
  userId: string,
  msFromAttempt: number
) => {
  trackEvent('checkout_session_create_succeeded', { sessionId, priceId, userId, msFromAttempt });
};

export const trackCheckoutSessionCreateFailed = (
  errorCode: string,
  errorMessage: string,
  priceId: string,
  userId: string
) => {
  trackEvent('checkout_session_create_failed', { errorCode, errorMessage, priceId, userId });
};

export const trackSignupToCheckoutAbandoned = (
  secondsOnPage: number,
  pageName: string,
  userId: string,
  scrollDepthPct: number
) => {
  trackEvent('signup_to_checkout_abandoned', { secondsOnPage, pageName, userId, scrollDepthPct });
};
